/**
 * Syntetisk Fokusgrupp - Main Application
 * Multi-agent AI-powered focus group simulator
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Play, FileText, Download, Settings, ChevronRight,
  MessageCircle, Brain, Sparkles, TrendingUp, AlertTriangle,
  Check, Loader2, RefreshCw, X, Lock, Search, Clock,
  Pause, RotateCcw, Filter, Copy, Share2, BookOpen, Volume2
} from 'lucide-react';
import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area, BarChart, Bar
} from 'recharts';

// Hooks and utilities
import { useSession } from './hooks/useSession';
import { useConfig, DEMOGRAPHIC_PRESETS } from './hooks/useConfig';
import { useModels } from './hooks/ModelsContext';
import { usePersonaChat } from './hooks/usePersonaChat';
import { useModeratorChat } from './hooks/useModeratorChat';
import { getSessionRecovery, clearSessionRecovery, getCustomDemographics, saveCustomDemographic, deleteCustomDemographic } from './utils/storage';

// Components
import { ConfigManager } from './components/ConfigManager';
import { PersonaCard } from './components/PersonaCard';
import { CostEstimator } from './components/CostEstimator';
import { CompareSetup, CompareRunning, CompareReport } from './components/CompareFlow';
import { StimulusSetup, StimulusRunning } from './components/StimulusFlow';
import { ReadingUpload, ReadingProgress, ReadingReview, ReadingLaunchConfig } from './components/ReadingFlow';
import { PersonasTab } from './components/PersonasTab';
import { UserGuide } from './components/UserGuide';
import { SessionHistory } from './components/SessionHistory';
import { PersonaDeepDive } from './components/PersonaDeepDive';
import { ModeratorChat } from './components/ModeratorChat';
import { AuthGate } from './components/AuthGate';
import { useTranslation, SUPPORTED_LANGUAGES } from './i18n/LanguageContext';
import { useCompare } from './hooks/useCompare';
import { useStimulus } from './hooks/useStimulus';
import { useReading } from './hooks/useReading';
import { usePersonaLibrary } from './hooks/usePersonaLibrary';

// API Configuration
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Session Recovery Dialog
function SessionRecoveryDialog({ recovery, onRestore, onDiscard }) {
  const { t, language } = useTranslation();
  if (!recovery) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
            <RotateCcw className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h3 className="font-medium text-lg">{t('recovery.title')}</h3>
            <p className="text-sm text-stone-500">{t('recovery.subtitle')}</p>
          </div>
        </div>

        <div className="bg-stone-50 rounded-lg p-4 mb-4">
          <div className="text-sm text-stone-600">
            <p><strong>{t('recovery.messages')}</strong> {recovery.messages?.length || 0}</p>
            <p><strong>{t('recovery.rounds')}</strong> {recovery.currentRound || 0}</p>
            <p><strong>{t('recovery.saved')}</strong> {new Date(recovery.savedAt).toLocaleString(language === 'fi' ? 'fi-FI' : language === 'en' ? 'en-US' : 'sv-SE')}</p>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onDiscard}
            className="flex-1 btn btn-secondary"
          >
            {t('recovery.discard')}
          </button>
          <button
            onClick={() => onRestore(recovery)}
            className="flex-1 btn btn-primary"
          >
            {t('recovery.restore')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Rate Limit Indicator
function RateLimitIndicator({ info }) {
  if (!info) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="fixed top-20 right-4 bg-amber-100 border border-amber-300 rounded-lg p-4 shadow-lg z-40"
    >
      <div className="flex items-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
        <div>
          <p className="font-medium text-amber-800">API rate limit</p>
          <p className="text-sm text-amber-600">{info.message}</p>
        </div>
      </div>
    </motion.div>
  );
}

// Main App Component
function App() {
  const { t, language, setLanguage } = useTranslation();

  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);
  const [recoveryData, setRecoveryData] = useState(null);
  const [appMode, setAppMode] = useState('focus-group'); // 'focus-group' | 'compare' | 'personas' | 'guide'

  // Use custom hooks
  const session = useSession();
  const configManager = useConfig();
  const compare = useCompare();
  const stimulus = useStimulus();
  const reading = useReading();
  const library = usePersonaLibrary();
  const personaChat = usePersonaChat();
  const moderatorChat = useModeratorChat();

  // Check for session recovery on mount
  useEffect(() => {
    const recovery = session.checkForRecovery();
    if (recovery) {
      setRecoveryData(recovery);
      setShowRecoveryDialog(true);
    }
  }, []);

  // Handle session recovery
  const handleRestore = (recovery) => {
    session.restoreSession(recovery);
    setShowRecoveryDialog(false);
    setRecoveryData(null);
  };

  const handleDiscardRecovery = () => {
    clearSessionRecovery();
    setShowRecoveryDialog(false);
    setRecoveryData(null);
  };

  // Show login screen if password is required
  return (
    <AuthGate>
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-amber-50">
        {/* Session Recovery Dialog */}
        {showRecoveryDialog && (
          <SessionRecoveryDialog
            recovery={recoveryData}
            onRestore={handleRestore}
            onDiscard={handleDiscardRecovery}
          />
        )}

        {/* Rate Limit Indicator */}
        <AnimatePresence>
          {session.rateLimitInfo && (
            <RateLimitIndicator info={session.rateLimitInfo} />
          )}
        </AnimatePresence>

        {/* Header */}
        <header className="bg-white/80 backdrop-blur-sm border-b border-stone-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-serif text-xl font-semibold text-stone-900">{t('app.title')}</h1>
                <p className="text-xs text-stone-500">{t('app.subtitle')}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Tab navigation - show in setup, personas, guide, or history mode */}
              {(appMode === 'personas' || appMode === 'guide' || appMode === 'history' || (session.stage === 'setup' && compare.compareStage === 'setup' && stimulus.stimulusStage === 'setup' && reading.stage === 'upload')) && (
                <div className="flex bg-stone-100 rounded-lg p-1 gap-1">
                  <button
                    onClick={() => setAppMode('focus-group')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${appMode === 'focus-group' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                      }`}
                  >
                    {t('compare.tabFocusGroup')}
                  </button>
                  <button
                    onClick={() => setAppMode('personas')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${appMode === 'personas' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                      }`}
                  >
                    {t('compare.tabPersonas')}
                  </button>
                  <button
                    onClick={() => setAppMode('compare')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${appMode === 'compare' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                      }`}
                  >
                    {t('compare.tabExperiment')}
                  </button>
                  <button
                    onClick={() => setAppMode('stimulus')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${appMode === 'stimulus' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                      }`}
                  >
                    {t('stimulus.tabLabel')}
                  </button>
                  <button
                    onClick={() => setAppMode('reading')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${appMode === 'reading' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                      }`}
                  >
                    {t('reading.tabLabel')}
                  </button>
                  <button
                    onClick={() => setAppMode('guide')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${appMode === 'guide' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                      }`}
                  >
                    {t('nav.guide')}
                  </button>
                  <button
                    onClick={() => setAppMode('history')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${appMode === 'history' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                      }`}
                  >
                    {t('nav.history')}
                  </button>
                </div>
              )}

              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="input text-sm py-2"
              >
                {SUPPORTED_LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>{lang.label}</option>
                ))}
              </select>
              {session.stage !== 'setup' && appMode === 'focus-group' && (
                <button
                  onClick={session.resetSession}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t('header.newSession')}
                </button>
              )}

              {appMode === 'compare' && compare.compareStage !== 'setup' && (
                <button
                  onClick={compare.resetComparison}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t('header.newSession')}
                </button>
              )}

              {appMode === 'stimulus' && stimulus.stimulusStage !== 'setup' && (
                <button
                  onClick={stimulus.resetStimulus}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t('header.newSession')}
                </button>
              )}

              {appMode === 'reading' && reading.stage !== 'upload' && (
                <button
                  onClick={reading.resetReading}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  {t('header.newSession')}
                </button>
              )}

              {session.stage === 'running' && !session.isGeneratingReport && !session.isPaused && (
                <button
                  onClick={session.pauseSession}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <Pause className="w-4 h-4" />
                  {t('header.pause')}
                </button>
              )}

              {session.stage === 'running' && session.isPaused && (
                <button
                  onClick={session.resumeSession}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  {t('header.resume')}
                </button>
              )}

              {session.stage === 'report' && session.sessionId && (
                <div className="flex gap-2">
                  <button
                    onClick={session.exportPDF}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    PDF
                  </button>
                  <button
                    onClick={session.exportDOCX}
                    className="btn btn-secondary flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    Word
                  </button>
                  <button
                    onClick={session.exportAudio}
                    className="btn btn-secondary flex items-center gap-2"
                  >
                    <Volume2 className="w-4 h-4" />
                    MP3
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Progress indicator (focus-group mode only) */}
        {appMode === 'focus-group' && session.stage !== 'setup' && (
          <div className="bg-white border-b border-stone-200">
            <div className="max-w-7xl mx-auto px-6 py-3">
              <div className="flex items-center gap-4">
                {/* Discussion step */}
                <div className={`flex items-center gap-2 ${session.stage === 'running' && !session.isGeneratingReport ? 'text-amber-600' :
                  ['guidance', 'report'].includes(session.stage) || session.isGeneratingReport ? 'text-green-600' : 'text-stone-400'
                  }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${session.stage === 'running' && !session.isGeneratingReport ? 'bg-amber-100' :
                    ['guidance', 'report'].includes(session.stage) || session.isGeneratingReport ? 'bg-green-100' : 'bg-stone-100'
                    }`}>
                    {session.stage === 'running' && !session.isGeneratingReport ? (
                      session.isPaused ? <Pause className="w-4 h-4" /> : <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                  </div>
                  <span className="font-medium">
                    {t('progress.discussion')} {session.isPaused && t('progress.paused')}
                  </span>
                </div>

                <ChevronRight className="w-4 h-4 text-stone-300" />

                {/* Guidance step */}
                <div className={`flex items-center gap-2 ${session.stage === 'guidance' ? 'text-amber-600' :
                  session.stage === 'report' || session.isGeneratingReport ? 'text-green-600' : 'text-stone-400'
                  }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${session.stage === 'guidance' ? 'bg-amber-100' :
                    session.stage === 'report' || session.isGeneratingReport ? 'bg-green-100' : 'bg-stone-100'
                    }`}>
                    {session.stage === 'guidance' ? (
                      <Sparkles className="w-4 h-4" />
                    ) : session.stage === 'report' || session.isGeneratingReport ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                  </div>
                  <span className="font-medium">{t('progress.guidance')}</span>
                </div>

                <ChevronRight className="w-4 h-4 text-stone-300" />

                {/* Report step */}
                <div className={`flex items-center gap-2 ${session.stage === 'report' ? 'text-amber-600' :
                  session.isGeneratingReport ? 'text-amber-600' : 'text-stone-400'
                  }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${session.stage === 'report' ? 'bg-amber-100' :
                    session.isGeneratingReport ? 'bg-amber-100' : 'bg-stone-100'
                    }`}>
                    {session.isGeneratingReport ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4" />
                    )}
                  </div>
                  <span className="font-medium">{t('progress.report')}</span>
                </div>

                {session.currentRound > 0 && session.stage === 'running' && !session.isGeneratingReport && (
                  <div className="ml-auto text-sm text-stone-600">
                    {t('progress.roundOf', { current: session.currentRound, total: configManager.config.rounds })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Error display */}
        <AnimatePresence>
          {(session.error || compare.error) && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-7xl mx-auto px-6 pt-4"
            >
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <span className="text-red-700">{session.error || compare.error}</span>
                </div>
                <button onClick={() => { session.setError(null); compare.setError(null); }} className="text-red-400 hover:text-red-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main content */}
        <main className="max-w-7xl mx-auto px-6 py-8">
          {appMode === 'history' ? (
            <SessionHistory ReportStage={ReportStage} personaChat={personaChat} moderatorChat={moderatorChat} />
          ) : appMode === 'guide' ? (
            <UserGuide />
          ) : appMode === 'personas' ? (
            <PersonasTab library={library} />
          ) : appMode === 'stimulus' ? (
            /* Stimulus mode */
            <AnimatePresence mode="wait">
              {stimulus.stimulusStage === 'setup' && (
                <motion.div key="stimulus-setup" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                  <StimulusSetup
                    stimulus={stimulus}
                    config={configManager.config}
                    personas={configManager.personas}
                    updateConfig={configManager.updateConfig}
                    generatePersonas={configManager.generatePersonas}
                    isGeneratingPersonas={configManager.isGeneratingPersonas}
                    library={library}
                  />
                </motion.div>
              )}
              {stimulus.stimulusStage === 'running' && (
                <motion.div key="stimulus-running" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                  <StimulusRunning
                    stimulus={stimulus}
                    config={configManager.config}
                    personas={configManager.personas}
                    RunningStage={RunningStage}
                    messagesEndRef={session.messagesEndRef}
                  />
                </motion.div>
              )}
              {stimulus.stimulusStage === 'guidance' && (
                <motion.div key="stimulus-guidance" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                  {/* Stimulus reference box */}
                  <div className="max-w-3xl mx-auto mb-6">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-medium text-amber-800">
                          {t('stimulus.referenceLabel')}{stimulus.stimulusLabel ? ` — ${stimulus.stimulusLabel}` : ''}
                        </span>
                      </div>
                      <p className="text-sm text-amber-900 whitespace-pre-wrap leading-relaxed">{stimulus.stimulusText}</p>
                    </div>
                  </div>
                  <GuidanceStage
                    moderatorSummary={stimulus.moderatorSummary}
                    onGenerate={(g) => stimulus.generateReport(g)}
                    onSkip={() => stimulus.generateReport('')}
                    isGeneratingReport={stimulus.isGeneratingReport}
                  />
                </motion.div>
              )}
              {stimulus.stimulusStage === 'report' && (
                <motion.div key="stimulus-report" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                  <ReportStage
                    config={configManager.config}
                    personas={configManager.personas}
                    messages={stimulus.messages}
                    roundSummaries={stimulus.roundSummaries}
                    finalAnalysis={stimulus.finalAnalysis}
                    moderatorSummary={stimulus.moderatorSummary}
                    beliefStates={stimulus.beliefStates}
                    sessionId={stimulus.sessionId}
                    personaChat={personaChat}
                    moderatorChat={moderatorChat}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          ) : appMode === 'reading' ? (
            /* Reading Panel mode */
            <AnimatePresence mode="wait">
              {reading.stage === 'upload' && (
                <motion.div key="reading-upload" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                  <ReadingUpload reading={reading} library={library} />
                </motion.div>
              )}
              {reading.stage === 'reading' && (
                <motion.div key="reading-progress" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                  <ReadingProgress reading={reading} />
                </motion.div>
              )}
              {reading.stage === 'review' && (
                <motion.div key="reading-review" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                  <ReadingReview reading={reading} onLaunch={() => reading.setStage('launch')} />
                </motion.div>
              )}
              {reading.stage === 'launch' && (
                <motion.div key="reading-launch" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                  <ReadingLaunchConfig
                    reading={reading}
                    config={configManager.config}
                    updateConfig={configManager.updateConfig}
                    onStart={(launchConfig) => reading.launchFocusGroup(launchConfig)}
                  />
                </motion.div>
              )}
              {reading.stage === 'running' && (
                <motion.div key="reading-running" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                  <RunningStage
                    messages={reading.messages}
                    currentSpeaker={reading.currentSpeaker}
                    streamingContent={reading.streamingContent}
                    currentRound={reading.currentRound}
                    roundSummaries={reading.roundSummaries}
                    config={configManager.config}
                    personas={configManager.personas}
                    messagesEndRef={session.messagesEndRef}
                  />
                </motion.div>
              )}
              {reading.stage === 'guidance' && (
                <motion.div key="reading-guidance" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                  <GuidanceStage
                    moderatorSummary={reading.moderatorSummary}
                    onGenerate={(g) => reading.generateReport(g)}
                    onSkip={() => reading.generateReport('')}
                    isGeneratingReport={reading.isGeneratingReport}
                  />
                </motion.div>
              )}
              {reading.stage === 'report' && (
                <motion.div key="reading-report" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                  <ReportStage
                    config={configManager.config}
                    personas={configManager.personas}
                    messages={reading.messages}
                    roundSummaries={reading.roundSummaries}
                    finalAnalysis={reading.finalAnalysis}
                    moderatorSummary={reading.moderatorSummary}
                    beliefStates={reading.beliefStates}
                    sessionId={reading.sessionId}
                    personaChat={personaChat}
                    moderatorChat={moderatorChat}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          ) : appMode === 'compare' ? (
            /* Compare / A/B mode */
            <AnimatePresence mode="wait">
              {compare.compareStage === 'setup' && (
                <motion.div key="compare-setup" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                  <CompareSetup
                    compare={compare}
                    config={configManager.config}
                    personas={configManager.personas}
                    updateConfig={configManager.updateConfig}
                    generatePersonas={configManager.generatePersonas}
                    isGeneratingPersonas={configManager.isGeneratingPersonas}
                  />
                </motion.div>
              )}
              {(compare.compareStage === 'running-a' || compare.compareStage === 'running-b' || compare.compareStage === 'generating') && (
                <motion.div key="compare-running" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                  <CompareRunning compare={compare} personas={configManager.personas} />
                </motion.div>
              )}
              {compare.compareStage === 'report' && (
                <motion.div key="compare-report" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                  <CompareReport compare={compare} personas={configManager.personas} />
                </motion.div>
              )}
            </AnimatePresence>
          ) : (
            /* Focus Group mode */
            <AnimatePresence mode="wait">
              {session.stage === 'setup' && (
                <motion.div
                  key="setup"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <SetupStage
                    config={configManager.config}
                    updateConfig={configManager.updateConfig}
                    personas={configManager.personas}
                    setPersonas={configManager.setPersonas}
                    selectedModels={configManager.selectedModels}
                    setSelectedModels={configManager.setSelectedModels}
                    toggleModel={configManager.toggleModel}
                    toggleGroup={configManager.toggleGroup}
                    isGroupSelected={configManager.isGroupSelected}
                    isGroupPartial={configManager.isGroupPartial}
                    generatePersonas={configManager.generatePersonas}
                    isGeneratingPersonas={configManager.isGeneratingPersonas}
                    updatePersona={configManager.updatePersona}
                    removePersona={configManager.removePersona}
                    regeneratePersona={configManager.regeneratePersona}
                    addQuestion={configManager.addQuestion}
                    updateQuestion={configManager.updateQuestion}
                    removeQuestion={configManager.removeQuestion}
                    savedConfigs={configManager.savedConfigs}
                    saveCurrentConfig={configManager.saveCurrentConfig}
                    loadSavedConfig={configManager.loadSavedConfig}
                    deleteSavedConfig={configManager.deleteSavedConfig}
                    exportConfig={configManager.exportConfig}
                    importConfig={configManager.importConfig}
                    modelStatus={configManager.modelStatus}
                    library={library}
                    startSession={(personasOverride) => session.startSession(configManager.config, personasOverride || configManager.personas)}
                    setError={session.setError}
                  />
                </motion.div>
              )}

              {session.stage === 'running' && (
                <motion.div
                  key="running"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <RunningStage
                    config={configManager.config}
                    personas={configManager.personas}
                    messages={session.messages}
                    currentRound={session.currentRound}
                    currentSpeaker={session.currentSpeaker}
                    streamingContent={session.streamingContent}
                    roundSummaries={session.roundSummaries}
                    messagesEndRef={session.messagesEndRef}
                    isGeneratingReport={session.isGeneratingReport}
                    isPaused={session.isPaused}
                    operatorGuidance={session.operatorGuidance}
                    onGuidanceChange={session.setOperatorGuidance}
                    onSendGuidance={session.sendOperatorGuidance}
                  />
                </motion.div>
              )}

              {session.stage === 'guidance' && (
                <motion.div
                  key="guidance"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <GuidanceStage
                    moderatorSummary={session.moderatorSummary}
                    onGenerate={(guidance) => session.generateReport(guidance)}
                    onSkip={() => session.generateReport('')}
                    isGeneratingReport={session.isGeneratingReport}
                  />
                </motion.div>
              )}

              {session.stage === 'report' && (
                <motion.div
                  key="report"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <ReportStage
                    config={configManager.config}
                    personas={configManager.personas}
                    messages={session.messages}
                    roundSummaries={session.roundSummaries}
                    finalAnalysis={session.finalAnalysis}
                    moderatorSummary={session.moderatorSummary}
                    beliefStates={session.beliefStates}
                    topicDimensions={session.topicDimensions}
                    groupDynamics={session.groupDynamics}
                    personaTrajectories={session.personaTrajectories}
                    sessionId={session.sessionId}
                    personaChat={personaChat}
                    moderatorChat={moderatorChat}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </main>
      </div>
    </AuthGate>
  );
}

// =============================================================================
// Setup Stage Component
// =============================================================================

function SetupStage({
  config,
  updateConfig,
  personas,
  setPersonas,
  selectedModels,
  setSelectedModels,
  toggleModel,
  toggleGroup,
  isGroupSelected,
  isGroupPartial,
  generatePersonas,
  isGeneratingPersonas,
  updatePersona,
  removePersona,
  regeneratePersona,
  addQuestion,
  updateQuestion,
  removeQuestion,
  savedConfigs,
  saveCurrentConfig,
  loadSavedConfig,
  deleteSavedConfig,
  exportConfig,
  importConfig,
  modelStatus,
  library,
  startSession,
  setError,
}) {
  const { t } = useTranslation();
  const { models: MODELS, modelGroups: MODEL_GROUPS, resolveModel } = useModels();
  const [customDemographics, setCustomDemographics] = useState(getCustomDemographics());
  const [showCustomDemoDialog, setShowCustomDemoDialog] = useState(false);
  const [newDemoName, setNewDemoName] = useState('');
  const [newDemoDesc, setNewDemoDesc] = useState('');
  const [panelSource, setPanelSource] = useState('generate'); // 'generate' | 'library'
  const [selectedPanelId, setSelectedPanelId] = useState('');
  const [panelModelAssignments, setPanelModelAssignments] = useState({});
  const modelIds = Object.keys(MODELS);
  const getPanelModel = (personaId, index) => {
    if (panelModelAssignments[personaId]) return panelModelAssignments[personaId];
    const persona = library.panelPersonas.find(p => p.id === personaId);
    if (persona?.default_model) {
      const resolved = resolveModel(persona.default_model);
      if (MODELS[resolved]) return resolved;
    }
    return selectedModels[index % selectedModels.length] || modelIds[index % modelIds.length];
  };

  const allDemographicPresets = [
    ...DEMOGRAPHIC_PRESETS.filter(p => p.id !== 'custom'),
    ...Object.values(customDemographics).map(d => ({
      id: d.id,
      name: d.name,
      description: d.description,
      isCustom: true,
    })),
    DEMOGRAPHIC_PRESETS.find(p => p.id === 'custom'),
  ];

  const handleGeneratePersonas = async () => {
    const result = await generatePersonas();
    if (result.error) {
      setError(result.error);
    }
  };

  const handleSaveCustomDemographic = () => {
    if (!newDemoName.trim() || !config.customDemographic.trim()) return;
    saveCustomDemographic(newDemoName.trim(), newDemoDesc.trim(), config.customDemographic);
    setCustomDemographics(getCustomDemographics());
    setShowCustomDemoDialog(false);
    setNewDemoName('');
    setNewDemoDesc('');
  };

  const handleDeleteCustomDemographic = (id) => {
    if (confirm(t('demographics.deleteConfirm'))) {
      deleteCustomDemographic(id);
      setCustomDemographics(getCustomDemographics());
    }
  };

  return (
    <div className="space-y-8">
      {/* Config Manager */}
      <div className="flex justify-end">
        <ConfigManager
          savedConfigs={savedConfigs}
          onSave={saveCurrentConfig}
          onLoad={loadSavedConfig}
          onDelete={deleteSavedConfig}
          onExport={exportConfig}
          onImport={importConfig}
        />
      </div>

      {/* Topic Configuration */}
      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        <h2 className="font-serif text-2xl mb-6 flex items-center gap-3">
          <MessageCircle className="w-6 h-6 text-amber-600" />
          {t('setup.topicSection')}
        </h2>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              {t('setup.titleLabel')}
            </label>
            <input
              type="text"
              value={config.title}
              onChange={e => updateConfig('title', e.target.value)}
              placeholder={t('setup.titlePlaceholder')}
              className="input w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              {t('setup.topicLabel')}
            </label>
            <input
              type="text"
              value={config.topic}
              onChange={e => updateConfig('topic', e.target.value)}
              placeholder={t('setup.topicPlaceholder')}
              className="input w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              {t('setup.descriptionLabel')}
            </label>
            <textarea
              value={config.description}
              onChange={e => updateConfig('description', e.target.value)}
              placeholder={t('setup.descriptionPlaceholder')}
              rows={3}
              className="input w-full"
            />
          </div>

          {/* Questions */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              {t('setup.questionsLabel')}
            </label>
            <div className="space-y-2">
              {config.questions.map((q, idx) => (
                <div key={idx} className="flex gap-2">
                  <input
                    type="text"
                    value={q}
                    onChange={e => updateQuestion(idx, e.target.value)}
                    placeholder={t('setup.questionPlaceholder', { n: idx + 1 })}
                    className="input flex-1"
                  />
                  {config.questions.length > 1 && (
                    <button
                      onClick={() => removeQuestion(idx)}
                      className="px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={addQuestion}
              className="mt-2 text-sm text-amber-600 hover:text-amber-700"
            >
              {t('setup.addQuestion')}
            </button>
          </div>

          {/* Moderator Briefing */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              {t('setup.briefingLabel')}
              <span className="text-stone-400 font-normal ml-2">{t('setup.briefingConfidential')}</span>
            </label>
            <textarea
              value={config.moderatorBriefing}
              onChange={e => updateConfig('moderatorBriefing', e.target.value)}
              placeholder={t('setup.briefingPlaceholder')}
              rows={3}
              className="input w-full"
            />
            <p className="text-xs text-stone-500 mt-1">
              {t('setup.briefingInfo')}
            </p>
          </div>

          {/* Direction Guidance */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              {t('setup.directionLabel')}
              <span className="text-stone-400 font-normal ml-2">{t('setup.directionOptional')}</span>
            </label>
            <textarea
              value={config.moderatorDirection}
              onChange={e => updateConfig('moderatorDirection', e.target.value)}
              placeholder={t('setup.directionPlaceholder')}
              rows={2}
              className="input w-full"
            />
            <p className="text-xs text-stone-500 mt-1">
              {t('setup.directionInfo')}
            </p>
          </div>

          {/* Avoid / Tone Down */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              {t('setup.avoidLabel')}
              <span className="text-stone-400 font-normal ml-2">{t('setup.avoidOptional')}</span>
            </label>
            <textarea
              value={config.moderatorAvoid}
              onChange={e => updateConfig('moderatorAvoid', e.target.value)}
              placeholder={t('setup.avoidPlaceholder')}
              rows={2}
              className="input w-full"
            />
            <p className="text-xs text-stone-500 mt-1">
              {t('setup.avoidInfo')}
            </p>
          </div>
        </div>
      </section>

      {/* Model Selection */}
      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        <h2 className="font-serif text-2xl mb-6 flex items-center gap-3">
          <Brain className="w-6 h-6 text-amber-600" />
          {t('models.title')}
        </h2>
        <p className="text-stone-600 mb-6">
          {t('models.description')}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(MODEL_GROUPS).map(([group, models]) => {
            const providerKey = group.toLowerCase();
            const isAvailable = modelStatus[providerKey] !== false;

            return (
              <div key={group} className="space-y-3">
                {/* Group header with toggle */}
                <button
                  onClick={() => toggleGroup(group)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${!isAvailable ? 'opacity-50 cursor-not-allowed border-stone-200' :
                    isGroupSelected(group)
                      ? 'border-amber-500 bg-amber-50'
                      : isGroupPartial(group)
                        ? 'border-amber-300 bg-amber-25'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  disabled={!isAvailable}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-stone-900">{group}</span>
                    {!isAvailable && (
                      <span className="text-xs text-red-500">{t('models.notConfigured')}</span>
                    )}
                  </div>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isGroupSelected(group)
                    ? 'bg-amber-500 border-amber-500'
                    : isGroupPartial(group)
                      ? 'bg-amber-200 border-amber-400'
                      : 'border-stone-300'
                    }`}>
                    {(isGroupSelected(group) || isGroupPartial(group)) && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                </button>

                {/* Individual models */}
                <div className="space-y-2 pl-2">
                  {models.map(modelId => {
                    const model = MODELS[modelId];
                    const isSelected = selectedModels.includes(modelId);

                    return (
                      <button
                        key={modelId}
                        onClick={() => toggleModel(modelId)}
                        disabled={!isAvailable}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-all ${!isAvailable ? 'opacity-50 cursor-not-allowed' :
                          isSelected
                            ? 'bg-stone-100'
                            : 'hover:bg-stone-50'
                          }`}
                      >
                        <div
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isSelected
                            ? 'border-amber-500 bg-amber-500'
                            : 'border-stone-300'
                            }`}
                        >
                          {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                        </div>
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: model.color }}
                        />
                        <span className="text-sm text-stone-700">{model.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 text-sm text-stone-500">
          {t('models.selectedCount', { count: selectedModels.length, plural: selectedModels.length !== 1 ? 'er' : '', pluralA: selectedModels.length !== 1 ? 'a' : '' })}
          {selectedModels.length < 2 && (
            <span className="text-amber-600 ml-2">
              {t('models.minWarning')}
            </span>
          )}
        </div>
      </section>

      {/* Panel / Participants */}
      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        <h2 className="font-serif text-2xl mb-6 flex items-center gap-3">
          <Users className="w-6 h-6 text-amber-600" />
          {t('setup.panelSection')}
        </h2>

        {/* Source toggle */}
        <div className="flex bg-stone-100 rounded-lg p-1 gap-1 mb-6 w-fit">
          <button
            onClick={() => setPanelSource('generate')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${panelSource === 'generate' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
              }`}
          >
            {t('setup.panelSourceGenerate')}
          </button>
          <button
            onClick={() => setPanelSource('library')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${panelSource === 'library' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
              }`}
          >
            {t('setup.panelSourceLibrary')}
          </button>
        </div>

        {panelSource === 'library' ? (
          /* Saved panel selection */
          <div className="space-y-4">
            {library.panels.length > 0 ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    {t('setup.selectPanel')}
                  </label>
                  <select
                    value={selectedPanelId}
                    onChange={async (e) => {
                      const panelId = e.target.value;
                      setSelectedPanelId(panelId);
                      if (panelId) {
                        await library.loadPanel(panelId);
                      }
                    }}
                    className="input w-full"
                  >
                    <option value="">{t('setup.selectPanel')}...</option>
                    {library.panels.map(panel => (
                      <option key={panel.id} value={panel.id}>
                        {panel.name} ({(panel.persona_ids || []).length} {t('config.participants')})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedPanelId && library.panelPersonas.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      {t('setup.panelPreview')}
                      <span className="text-stone-400 font-normal ml-2">
                        {t('setup.panelPersonaCount', { count: library.panelPersonas.length })}
                      </span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {library.panelPersonas.map((persona, idx) => {
                        const assignedModelId = getPanelModel(persona.id, idx);
                        const assignedModel = MODELS[assignedModelId] || { name: assignedModelId, color: '#888' };
                        return (
                          <div key={persona.id} className="p-3 bg-stone-50 rounded-lg border border-stone-200 flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-stone-800 text-sm">{persona.name}</div>
                              <div className="text-xs text-stone-500 mt-0.5">
                                {persona.age && `${persona.age} ${t('persona.years')}`}
                                {persona.region && ` · ${persona.region}`}
                                {persona.employment && ` · ${persona.employment}`}
                              </div>
                            </div>
                            <select
                              value={assignedModelId}
                              onChange={(e) => setPanelModelAssignments(prev => ({ ...prev, [persona.id]: e.target.value }))}
                              className="text-xs rounded-full px-2 py-1 text-white border-0 cursor-pointer flex-shrink-0"
                              style={{ backgroundColor: assignedModel.color }}
                            >
                              {Object.entries(MODELS).map(([id, m]) => (
                                <option key={id} value={id} style={{ color: '#000', backgroundColor: '#fff' }}>{m.name}</option>
                              ))}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-stone-500 text-sm">{t('setup.noSavedPanels')}</p>
            )}
          </div>
        ) : (
          /* Generate mode - existing demographics flow */
          <div className="space-y-6">
            {/* Participant count slider */}
            <div className="max-w-xs">
              <label className="block text-sm font-medium text-stone-700 mb-2">
                {t('session.participantsLabel', { n: config.participantCount })}
              </label>
              <input
                type="range"
                min="4"
                max="8"
                value={config.participantCount}
                onChange={e => updateConfig('participantCount', parseInt(e.target.value))}
                className="w-full accent-amber-500"
              />
              <div className="flex justify-between text-xs text-stone-500 mt-1">
                <span>4</span>
                <span>8</span>
              </div>
            </div>

            {/* Demographic presets */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {allDemographicPresets.map(preset => (
                <button
                  key={preset.id}
                  onClick={() => updateConfig('demographicPreset', preset.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all relative ${config.demographicPreset === preset.id
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-stone-200 hover:border-stone-300'
                    }`}
                >
                  <div className="font-medium text-stone-900">{preset.isCustom ? preset.name : t(`demographics.preset.${preset.id}`)}</div>
                  <div className="text-sm text-stone-500 mt-1">{preset.isCustom ? preset.description : t(`demographics.preset.${preset.id}.desc`)}</div>
                  {preset.isCustom && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCustomDemographic(preset.id);
                      }}
                      className="absolute top-2 right-2 p-1 text-stone-400 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </button>
              ))}
            </div>

            {config.demographicPreset === 'custom' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    {t('demographics.describeLabel')}
                  </label>
                  <textarea
                    value={config.customDemographic}
                    onChange={e => updateConfig('customDemographic', e.target.value)}
                    placeholder={t('demographics.describePlaceholder')}
                    rows={3}
                    className="input w-full"
                  />
                </div>

                {config.customDemographic.trim() && (
                  <button
                    onClick={() => setShowCustomDemoDialog(true)}
                    className="text-sm text-amber-600 hover:text-amber-700"
                  >
                    {t('demographics.saveProfile')}
                  </button>
                )}
              </div>
            )}

            <div>
              <button
                onClick={handleGeneratePersonas}
                disabled={isGeneratingPersonas}
                className="btn btn-secondary flex items-center gap-2"
              >
                {isGeneratingPersonas ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('demographics.generating')}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    {t('demographics.generate')}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Workshop Settings */}
      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        <h2 className="font-serif text-2xl mb-6 flex items-center gap-3">
          <Settings className="w-6 h-6 text-amber-600" />
          {t('setup.workshopSection')}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              {t('session.roundsLabel', { n: config.rounds })}
            </label>
            <input
              type="range"
              min="3"
              max="20"
              value={config.rounds}
              onChange={e => updateConfig('rounds', parseInt(e.target.value))}
              className="w-full accent-amber-500"
            />
            <div className="flex justify-between text-xs text-stone-500 mt-1">
              <span>{t('session.roundsMin')}</span>
              <span>{t('session.roundsMax')}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              {t('session.moderatorModel')}
            </label>
            <select
              value={config.moderatorModel}
              onChange={e => updateConfig('moderatorModel', e.target.value)}
              className="input w-full"
            >
              {Object.entries(MODELS).map(([id, model]) => (
                <option key={id} value={id}>{model.name} ({model.provider})</option>
              ))}
            </select>
          </div>
        </div>

        {/* Devil's Advocate Toggle */}
        <div className="mt-6 pt-6 border-t border-stone-100 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config.devilsAdvocate || false}
              onChange={e => updateConfig('devilsAdvocate', e.target.checked)}
              className="mt-1 w-4 h-4 accent-amber-500"
            />
            <div>
              <span className="font-medium text-stone-800">{t('session.devilsAdvocate')}</span>
              <p className="text-sm text-stone-500 mt-0.5">{t('session.devilsAdvocateDesc')}</p>
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config.enhancedBeliefTracking || false}
              onChange={e => updateConfig('enhancedBeliefTracking', e.target.checked)}
              className="mt-1 w-4 h-4 accent-indigo-500"
            />
            <div>
              <span className="font-medium text-stone-800">{t('session.enhancedBeliefTracking')}</span>
              <p className="text-sm text-stone-500 mt-0.5">{t('session.enhancedBeliefTrackingDesc')}</p>
            </div>
          </label>
        </div>
      </section>

      {/* Participants Preview */}
      {panelSource === 'generate' && personas.length > 0 && (
        <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
          <h2 className="font-serif text-2xl mb-6 flex items-center gap-3">
            <Brain className="w-6 h-6 text-amber-600" />
            {t('personas.title')}
            <span className="text-sm font-normal text-stone-500">
              {t('personas.count', { count: personas.length })}
            </span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {personas.map(persona => (
              <PersonaCard
                key={persona.id}
                persona={persona}
                onUpdate={updatePersona}
                onRemove={removePersona}
                onRegenerate={regeneratePersona}
              />
            ))}
          </div>
        </section>
      )}

      {/* Start Button */}
      {((panelSource === 'generate' && personas.length > 0) ||
        (panelSource === 'library' && selectedPanelId && library.panelPersonas.length > 0)) && (
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={() => {
                if (panelSource === 'library') {
                  // Map library personas to session format with user-assigned model
                  // Generate new unique IDs per session to avoid DB primary key conflicts
                  // (library persona IDs are reused across sessions)
                  const mappedPersonas = library.panelPersonas.map((p, idx) => {
                    const modelKey = getPanelModel(p.id, idx);
                    const modelInfo = MODELS[modelKey] || {};
                    return {
                      id: crypto.randomUUID().slice(0, 8),
                      library_persona_id: p.id,
                      name: p.name,
                      age: p.age || 35,
                      gender: p.gender || '',
                      region: p.region || '',
                      employment: p.employment || '',
                      education: p.education || '',
                      family_status: p.family_status || '',
                      personality_traits: p.personality_traits || p.traits || [],
                      background_story: p.background_story || p.background || '',
                      model_id: modelKey,
                      provider: modelInfo.provider || '',
                      values: p.values || [],
                      political_leaning: p.political_leaning || '',
                      worldview: p.worldview || '',
                      communication_style: p.communication_style || '',
                      argumentation_style: p.argumentation_style || '',
                      openness_to_change: p.openness_to_change ?? 5,
                    };
                  });
                  setPersonas(mappedPersonas);
                  startSession(mappedPersonas);
                } else {
                  startSession();
                }
              }}
              disabled={!config.topic}
              className={`btn text-lg px-8 py-4 flex items-center gap-3 ${config.topic
                ? 'btn-primary'
                : 'bg-stone-300 text-stone-500 cursor-not-allowed'
                }`}
            >
              <Play className="w-5 h-5" />
              {t('personas.start')}
            </button>
            {!config.topic && (
              <p className="text-amber-600 text-sm">
                {t('personas.topicRequired')}
              </p>
            )}
          </div>
        )}

      {/* Custom Demographic Save Dialog */}
      <AnimatePresence>
        {showCustomDemoDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setShowCustomDemoDialog(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-serif text-xl mb-4">{t('saveDemo.title')}</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    {t('saveDemo.nameLabel')}
                  </label>
                  <input
                    type="text"
                    value={newDemoName}
                    onChange={(e) => setNewDemoName(e.target.value)}
                    placeholder={t('saveDemo.namePlaceholder')}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    {t('saveDemo.descLabel')}
                  </label>
                  <input
                    type="text"
                    value={newDemoDesc}
                    onChange={(e) => setNewDemoDesc(e.target.value)}
                    placeholder={t('saveDemo.descPlaceholder')}
                    className="input w-full"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setShowCustomDemoDialog(false)}
                  className="btn btn-secondary"
                >
                  {t('saveDemo.cancel')}
                </button>
                <button
                  onClick={handleSaveCustomDemographic}
                  disabled={!newDemoName.trim()}
                  className="btn btn-primary"
                >
                  {t('saveDemo.save')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// =============================================================================
// Guidance Stage Component
// =============================================================================

function GuidanceStage({ moderatorSummary, onGenerate, onSkip, isGeneratingReport }) {
  const { t } = useTranslation();
  const [guidance, setGuidance] = useState('');

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* Moderator Summary */}
      {moderatorSummary && (
        <section className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-2xl p-8 text-white">
          <h2 className="font-serif text-2xl mb-4 flex items-center gap-3">
            <MessageCircle className="w-6 h-6 text-purple-300" />
            {t('guidance.summaryPreview')}
          </h2>
          <div className="text-purple-100 leading-relaxed whitespace-pre-wrap">
            {moderatorSummary}
          </div>
          <p className="text-purple-300 text-sm mt-4 italic">
            {t('guidance.summaryWillRegenerate')}
          </p>
        </section>
      )}

      {/* Guidance Input */}
      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-amber-100 rounded-full mb-4">
            <FileText className="w-7 h-7 text-amber-600" />
          </div>
          <h2 className="font-serif text-2xl mb-2">{t('guidance.title')}</h2>
          <p className="text-stone-500">{t('guidance.subtitle')}</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              {t('guidance.description')}
            </label>
            <textarea
              value={guidance}
              onChange={(e) => setGuidance(e.target.value)}
              placeholder={t('guidance.placeholder')}
              rows={4}
              className="input w-full"
              disabled={isGeneratingReport}
            />
          </div>

          <div className="flex gap-3 justify-center pt-4">
            <button
              onClick={onSkip}
              disabled={isGeneratingReport}
              className="btn btn-secondary flex items-center gap-2 px-6 py-3"
            >
              <ChevronRight className="w-4 h-4" />
              {t('guidance.skip')}
            </button>
            <button
              onClick={() => onGenerate(guidance)}
              disabled={isGeneratingReport}
              className="btn btn-primary flex items-center gap-2 px-6 py-3"
            >
              {isGeneratingReport ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('running.generatingReport')}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  {t('guidance.generate')}
                </>
              )}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

// =============================================================================
// Running Stage Component
// =============================================================================

function RunningStage({
  config,
  personas = [],
  messages = [],
  currentRound,
  currentSpeaker,
  streamingContent,
  roundSummaries = [],
  messagesEndRef,
  isGeneratingReport,
  isPaused,
  operatorGuidance,
  onGuidanceChange,
  onSendGuidance,
}) {
  const { t } = useTranslation();
  const { models: MODELS } = useModels();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Main conversation */}
      <div className="lg:col-span-3">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
          <div className="p-4 border-b border-stone-200 bg-stone-50">
            <h2 className="font-serif text-lg font-medium">{t('running.discussion')}</h2>
            <p className="text-sm text-stone-500">{config.topic}</p>
          </div>

          <div className="p-6 space-y-4 max-h-[600px] overflow-y-auto">
            {/* Already completed messages */}
            {messages.map((msg, idx) => (
              <MessageBubble key={msg.id || idx} message={msg} personas={personas} />
            ))}

            {/* Currently streaming message */}
            {currentSpeaker && streamingContent && (
              <MessageBubble
                message={{
                  speaker: currentSpeaker.name,
                  speakerId: currentSpeaker.id,
                  content: streamingContent,
                  round: currentSpeaker.round,
                  model: currentSpeaker.model,
                }}
                personas={personas}
                isStreaming={true}
              />
            )}

            {/* Loading indicator when speaker started but no content yet */}
            {currentSpeaker && !streamingContent && (
              <div className="flex items-center gap-3 text-stone-500 p-4 bg-stone-50 rounded-lg">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('running.typing', { name: currentSpeaker.name })}</span>
              </div>
            )}

            {/* Paused indicator with operator guidance */}
            {isPaused && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 space-y-3">
                <div className="flex items-center gap-3 text-amber-700">
                  <Pause className="w-5 h-5" />
                  <span className="font-medium">{t('running.paused')}</span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    {t('running.guidanceLabel')}
                  </label>
                  <textarea
                    value={operatorGuidance}
                    onChange={(e) => onGuidanceChange(e.target.value)}
                    placeholder={t('running.guidancePlaceholder')}
                    className="w-full p-3 border border-stone-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400"
                    rows={3}
                  />
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-xs text-stone-500">{t('running.guidanceHint')}</p>
                    <button
                      onClick={() => onSendGuidance(operatorGuidance)}
                      disabled={!operatorGuidance.trim()}
                      className="btn btn-secondary text-sm px-3 py-1 disabled:opacity-50"
                    >
                      {t('running.guidanceSend')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Report generation indicator */}
            {isGeneratingReport && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-3 text-amber-700 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200"
              >
                <Loader2 className="w-5 h-5 animate-spin" />
                <div>
                  <span className="font-medium">{t('running.generatingReport')}</span>
                  <p className="text-sm text-amber-600">{t('running.analyzingReport')}</p>
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        {/* Participants */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
          <h3 className="font-medium text-stone-900 mb-4">{t('running.participants')}</h3>
          <div className="space-y-3">
            {personas.map(persona => {
              const model = MODELS[persona.model_id] || {};
              const isSpeaking = currentSpeaker?.id === persona.id;

              return (
                <div
                  key={persona.id}
                  className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${isSpeaking ? 'bg-amber-50 ring-2 ring-amber-300' : ''
                    }`}
                >
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: model.color || '#888' }}
                  />
                  <span className={`flex-1 text-sm ${isSpeaking ? 'font-medium' : ''}`}>
                    {persona.name}
                  </span>
                  {isSpeaking && (
                    <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                  )}
                </div>
              );
            })}

            {/* Moderator */}
            <div className={`flex items-center gap-3 p-2 rounded-lg border-t border-stone-100 mt-2 pt-4 ${currentSpeaker?.id === 'moderator' ? 'bg-amber-50 ring-2 ring-amber-300' : ''
              }`}>
              <div className="w-3 h-3 rounded-full bg-stone-800" />
              <span className={`flex-1 text-sm ${currentSpeaker?.id === 'moderator' ? 'font-medium' : ''}`}>
                {t('running.moderator')}
              </span>
              {currentSpeaker?.id === 'moderator' && (
                <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
              )}
            </div>
          </div>
        </div>

        {/* Round Summaries */}
        {roundSummaries.length > 0 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200">
            <h3 className="font-medium text-stone-900 mb-4">{t('running.roundsSidebar')}</h3>
            <div className="space-y-4">
              {roundSummaries.map((summary, idx) => (
                <div key={idx} className="text-sm border-b border-stone-100 pb-3 last:border-0">
                  <div className="font-medium text-amber-600 mb-1">{t('running.round', { n: summary.round })}</div>
                  {summary.key_themes && summary.key_themes.length > 0 && (
                    <div className="text-stone-600">
                      {summary.key_themes.slice(0, 2).join(', ')}
                    </div>
                  )}
                  {summary.tensions && summary.tensions.length > 0 && (
                    <div className="text-red-600 text-xs mt-1">
                      {t('running.tensions', { count: summary.tensions.length })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Message Bubble Component
// =============================================================================

function MessageBubble({ message, personas, isStreaming }) {
  const { t } = useTranslation();
  const { models: MODELS } = useModels();
  const [copied, setCopied] = useState(false);
  const persona = personas.find(p => p.id === message.speakerId);
  const model = MODELS[message.model] || MODELS[persona?.model_id] || {};
  const isModerator = message.speaker === 'Moderator' || message.speakerId === 'moderator';

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex gap-4 group ${isModerator ? 'bg-stone-50 -mx-6 px-6 py-4 border-y border-stone-100' : ''}`}
    >
      <div
        className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-medium text-sm"
        style={{ backgroundColor: isModerator ? '#1c1917' : (model.color || '#888') }}
      >
        {message.speaker?.charAt(0) || '?'}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="font-medium text-stone-900">{message.speaker}</span>
          {!isModerator && model.name && (
            <span
              className="px-2 py-0.5 rounded text-xs text-white"
              style={{ backgroundColor: model.color || '#888' }}
            >
              {model.name}
            </span>
          )}
          {message.round !== undefined && message.round !== 0 && (
            <span className="text-xs text-stone-400">{t('message.round', { n: message.round })}</span>
          )}
          {isStreaming && (
            <span className="flex items-center gap-1 text-xs text-amber-600">
              <span className="inline-block w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
              {t('message.typing')}
            </span>
          )}

          {/* Copy button */}
          {!isStreaming && (
            <button
              onClick={handleCopy}
              className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-stone-100 rounded"
              title={t('message.copy')}
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4 text-stone-400" />
              )}
            </button>
          )}
        </div>

        <p className="text-stone-700 whitespace-pre-wrap leading-relaxed">{message.content}</p>

        {message.sentiment !== undefined && !isStreaming && (
          <div className="flex items-center gap-4 mt-2 text-xs">
            <span className={`flex items-center gap-1 ${message.sentiment > 0.3 ? 'text-green-600' :
              message.sentiment < -0.3 ? 'text-red-600' : 'text-stone-400'
              }`}>
              {message.sentiment > 0.3 ? '😊' : message.sentiment < -0.3 ? '😟' : '😐'}
              <span>{(message.sentiment * 100).toFixed(0)}%</span>
            </span>
            {message.keyPoints && message.keyPoints.length > 0 && (
              <span className="text-stone-400">
                {t('message.keyPoints', { count: message.keyPoints.length })}
              </span>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// =============================================================================
// Report Stage Component
// =============================================================================

function ReportStage({ config, personas, messages, roundSummaries, finalAnalysis, moderatorSummary, beliefStates = {}, topicDimensions = [], groupDynamics = [], personaTrajectories = {}, sessionId, personaChat, moderatorChat }) {
  const { t } = useTranslation();
  const { models: MODELS } = useModels();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRound, setSelectedRound] = useState(null);

  // Filter messages by search and round
  const filteredMessages = messages.filter(m => {
    const matchesSearch = !searchTerm ||
      m.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.speaker.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRound = selectedRound === null || m.round === selectedRound;
    return matchesSearch && matchesRound;
  });

  // Prepare sentiment data for chart
  const sentimentData = messages
    .filter(m => m.sentiment !== undefined)
    .map((m, idx) => ({
      index: idx + 1,
      sentiment: m.sentiment,
      speaker: m.speaker,
      round: m.round,
    }));

  // Timeline data
  const timelineData = roundSummaries.map(s => ({
    round: t('report.roundAbbr', { n: s.round }),
    themes: s.key_themes?.length || 0,
    tensions: s.tensions?.length || 0,
    consensus: s.consensus_points?.length || 0,
  }));

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-8"
    >
      {/* Title */}
      <div className="text-center">
        {config.title && <h1 className="font-serif text-4xl mb-2">{config.title}</h1>}
        <p className="text-xl text-stone-600">{config.topic}</p>
      </div>

      {/* Moderator Summary */}
      {moderatorSummary && (
        <section className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-2xl p-8 text-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl flex items-center gap-3">
              <MessageCircle className="w-6 h-6 text-purple-300" />
              {t('report.moderatorSummary')}
            </h2>
            {moderatorChat && (
              <button
                onClick={() => moderatorChat.startChat(sessionId)}
                className="flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 text-white rounded-xl text-sm font-medium transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                {t('moderatorChat.chatButton')}
              </button>
            )}
          </div>
          <div className="text-purple-100 leading-relaxed whitespace-pre-wrap">
            {moderatorSummary}
          </div>
        </section>
      )}

      {/* Executive Summary */}
      <section className="bg-gradient-to-br from-stone-900 to-stone-800 rounded-2xl p-8 text-white">
        <h2 className="font-serif text-3xl mb-4">{t('report.summary')}</h2>
        <p className="text-xl text-stone-300 leading-relaxed">
          {finalAnalysis?.executive_summary || t('report.generatingSummary')}
        </p>

        <div className="grid grid-cols-3 gap-6 mt-8">
          <div className="text-center p-4 bg-white/10 rounded-xl">
            <div className="text-3xl font-serif">{personas.length}</div>
            <div className="text-sm text-stone-400">{t('report.participants')}</div>
          </div>
          <div className="text-center p-4 bg-white/10 rounded-xl">
            <div className="text-3xl font-serif">{roundSummaries.length}</div>
            <div className="text-sm text-stone-400">{t('report.rounds')}</div>
          </div>
          <div className="text-center p-4 bg-white/10 rounded-xl">
            <div className="text-3xl font-serif">{messages.length}</div>
            <div className="text-sm text-stone-400">{t('report.messages')}</div>
          </div>
        </div>
      </section>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Sentiment Chart */}
        <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
          <h2 className="font-serif text-2xl mb-6 flex items-center gap-3">
            <TrendingUp className="w-6 h-6 text-amber-600" />
            {t('report.sentimentTitle')}
          </h2>
          {sentimentData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={sentimentData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="index" stroke="#737373" fontSize={12} />
                <YAxis domain={[-1, 1]} stroke="#737373" fontSize={12} />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload[0]) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white p-3 rounded-lg shadow-lg border border-stone-200">
                          <p className="font-medium">{data.speaker}</p>
                          <p className="text-sm text-stone-500">{t('report.sentimentTooltipRound', { n: data.round })}</p>
                          <p className="text-sm">{t('report.sentimentTooltipLabel')} {(data.sentiment * 100).toFixed(0)}%</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="sentiment"
                  stroke="#d97706"
                  fill="#fef3c7"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-stone-400">
              {t('report.noSentiment')}
            </div>
          )}
        </section>

        {/* Round Timeline */}
        <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
          <h2 className="font-serif text-2xl mb-6 flex items-center gap-3">
            <Clock className="w-6 h-6 text-amber-600" />
            {t('report.timelineTitle')}
          </h2>
          {timelineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={timelineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="round" stroke="#737373" fontSize={12} />
                <YAxis stroke="#737373" fontSize={12} />
                <Tooltip />
                <Bar dataKey="themes" fill="#f59e0b" name={t('report.themes')} />
                <Bar dataKey="tensions" fill="#ef4444" name={t('report.tensions')} />
                <Bar dataKey="consensus" fill="#22c55e" name={t('report.consensus')} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-stone-400">
              {t('report.noData')}
            </div>
          )}
        </section>
      </div>

      {/* Position Evolution (Belief States) */}
      {Object.keys(beliefStates).length > 0 && (
        <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
          <h2 className="font-serif text-2xl mb-6 flex items-center gap-3">
            <TrendingUp className="w-6 h-6 text-amber-600" />
            {t('report.beliefEvolution')}
          </h2>
          <div className="space-y-4">
            {personas.map(persona => {
              const beliefs = beliefStates[persona.id] || [];
              if (beliefs.length === 0) return null;
              const trajectory = personaTrajectories[persona.id];
              const trajectoryColors = {
                Anchor: 'bg-stone-100 text-stone-700',
                Drifter: 'bg-blue-100 text-blue-700',
                Convert: 'bg-purple-100 text-purple-700',
                Polarizer: 'bg-red-100 text-red-700',
                Bridger: 'bg-green-100 text-green-700',
                Explorer: 'bg-amber-100 text-amber-700',
              };
              return (
                <div key={persona.id} className="border border-stone-100 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    {sessionId && personaChat ? (
                      <button
                        onClick={() => personaChat.startDeepDive(sessionId, persona.id)}
                        className="font-medium text-stone-800 hover:text-purple-700 transition-colors cursor-pointer underline decoration-dotted underline-offset-2"
                      >
                        {persona.name}
                      </button>
                    ) : (
                      <span className="font-medium text-stone-800">{persona.name}</span>
                    )}
                    {trajectory && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${trajectoryColors[trajectory] || 'bg-stone-100 text-stone-600'}`}>
                        {t(`report.trajectory${trajectory}`) || trajectory}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {beliefs.map((b, idx) => {
                      const conviction = b.conviction_level || b.overall_conviction || 0.5;
                      const hasDimensions = b.dimensions && Object.keys(b.dimensions).length > 0;
                      return (
                        <div
                          key={idx}
                          className="flex-shrink-0 p-3 rounded-lg border border-amber-200 min-w-[200px]"
                          style={{ backgroundColor: `rgba(245, 158, 11, ${conviction * 0.2})` }}
                        >
                          <div className="text-xs font-medium text-amber-700 mb-1">R{b.round}</div>
                          {/* Dimension scores (enhanced mode) */}
                          {hasDimensions && (
                            <div className="mb-2 space-y-1">
                              {Object.entries(b.dimensions).map(([dimId, dimData]) => {
                                const score = typeof dimData === 'object' ? dimData.score : dimData;
                                const dimLabel = topicDimensions.find(d => d.id === dimId)?.label || dimId;
                                return (
                                  <div key={dimId} className="flex items-center gap-1.5">
                                    <span className="text-xs text-stone-500 w-20 truncate" title={dimLabel}>{dimLabel}</span>
                                    <div className="flex-1 h-1.5 bg-stone-200 rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-amber-500 rounded-full transition-all"
                                        style={{ width: `${(score || 0.5) * 100}%` }}
                                      />
                                    </div>
                                    <span className="text-xs text-stone-400 w-7 text-right">{((score || 0.5) * 100).toFixed(0)}%</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {/* Key positions (original mode or fallback) */}
                          {!hasDimensions && (b.key_positions || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {(b.key_positions || []).slice(0, 3).map((pos, pi) => (
                                <span key={pi} className="inline-block px-2 py-0.5 bg-white/70 rounded text-xs text-stone-700">
                                  {pos}
                                </span>
                              ))}
                            </div>
                          )}
                          {b.emotional_stance && (
                            <div className="text-xs text-stone-500">
                              {t('report.emotionalStance')}: {b.emotional_stance}
                            </div>
                          )}
                          <div className="text-xs text-stone-400 mt-1">
                            {t('report.beliefConviction')}: {(conviction * 100).toFixed(0)}%
                          </div>
                          {/* Shift trigger (enhanced mode) */}
                          {b.shift_from_previous && b.shift_from_previous.trigger && (
                            <div className="text-xs text-indigo-600 mt-1 italic">
                              {t('report.shiftTrigger')}: {b.shift_from_previous.trigger}
                            </div>
                          )}
                          {b.agreements && Object.keys(b.agreements).length > 0 && (
                            <div className="text-xs text-green-600 mt-1">
                              {t('report.beliefAgreements')}: {Object.entries(b.agreements).map(([k, v]) => `${k}`).join(', ')}
                            </div>
                          )}
                          {b.disagreements && Object.keys(b.disagreements).length > 0 && (
                            <div className="text-xs text-red-600 mt-1">
                              {t('report.beliefDisagreements')}: {Object.entries(b.disagreements).map(([k, v]) => `${k}`).join(', ')}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Group Dynamics (Enhanced Belief Tracking) */}
      {groupDynamics.length > 0 && (
        <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
          <h2 className="font-serif text-2xl mb-6 flex items-center gap-3">
            <Users className="w-6 h-6 text-indigo-600" />
            {t('report.groupDynamics')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Convergence & Polarization over rounds */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-stone-600 uppercase tracking-wide">{t('report.convergence')} / {t('report.polarization')}</h3>
              {groupDynamics.map((gd, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="text-xs text-stone-500 w-8">R{gd.round}</span>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-green-600 w-24">{t('report.convergence')}</span>
                      <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full" style={{ width: `${(gd.convergence_index || 0) * 100}%` }} />
                      </div>
                      <span className="text-xs text-stone-400 w-10 text-right">{((gd.convergence_index || 0) * 100).toFixed(0)}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-red-600 w-24">{t('report.polarization')}</span>
                      <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div className="h-full bg-red-400 rounded-full" style={{ width: `${(gd.polarization_index || 0) * 100}%` }} />
                      </div>
                      <span className="text-xs text-stone-400 w-10 text-right">{((gd.polarization_index || 0) * 100).toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Influence Map */}
            {(() => {
              const allInfluences = groupDynamics.flatMap(gd => gd.influence_graph || []);
              const allCoalitions = groupDynamics.length > 0 ? (groupDynamics[groupDynamics.length - 1].coalitions || []) : [];
              return (
                <div className="space-y-4">
                  {allInfluences.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-stone-600 uppercase tracking-wide mb-2">{t('report.influenceMap')}</h3>
                      <div className="space-y-1">
                        {allInfluences.map((inf, idx) => (
                          <div key={idx} className="text-xs text-stone-600 flex items-center gap-1">
                            <span className="font-medium">{inf.influencer}</span>
                            <span className="text-stone-400">{t('report.influenced')}</span>
                            <span className="font-medium">{inf.influenced}</span>
                            {inf.dimension && (
                              <span className="text-stone-400">{t('report.on')} {inf.dimension}</span>
                            )}
                            {inf.delta && (
                              <span className={`ml-1 ${inf.delta > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                ({inf.delta > 0 ? '+' : ''}{(inf.delta * 100).toFixed(0)}%)
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {allCoalitions.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-stone-600 uppercase tracking-wide mb-2">{t('report.coalitions')}</h3>
                      <div className="flex flex-wrap gap-2">
                        {allCoalitions.map((c, idx) => (
                          <div key={idx} className="px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-lg text-xs text-indigo-700">
                            {(c.members || []).join(' + ')}
                            {c.basis && <span className="text-indigo-400 ml-1">({c.basis})</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </section>
      )}

      {/* Key Insights */}
      {finalAnalysis?.key_insights && finalAnalysis.key_insights.length > 0 && (
        <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
          <h2 className="font-serif text-2xl mb-6 flex items-center gap-3">
            <Brain className="w-6 h-6 text-amber-600" />
            {t('report.insightsTitle')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {finalAnalysis.key_insights.map((insight, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="p-6 bg-gradient-to-br from-amber-50 to-white rounded-xl border border-amber-200"
              >
                <h3 className="font-medium text-stone-900 mb-2">
                  {typeof insight === 'string' ? insight : insight.insight}
                </h3>
                {typeof insight === 'object' && (
                  <>
                    {insight.supporting_evidence && (
                      <p className="text-sm text-stone-600 italic mb-2">
                        &ldquo;{insight.supporting_evidence}&rdquo;
                      </p>
                    )}
                    {insight.implication && (
                      <p className="text-sm text-stone-500">
                        → {insight.implication}
                      </p>
                    )}
                  </>
                )}
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Transcript with Search */}
      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl flex items-center gap-3">
            <MessageCircle className="w-6 h-6 text-amber-600" />
            {t('report.transcriptTitle')}
          </h2>

          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('report.searchPlaceholder')}
                className="input pl-10 w-64"
              />
            </div>

            {/* Round Filter */}
            <select
              value={selectedRound ?? ''}
              onChange={(e) => setSelectedRound(e.target.value ? parseInt(e.target.value) : null)}
              className="input"
            >
              <option value="">{t('report.allRounds')}</option>
              {roundSummaries.map(s => (
                <option key={s.round} value={s.round}>{t('report.roundFilter', { n: s.round })}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-4 max-h-[600px] overflow-y-auto">
          {filteredMessages.length > 0 ? (
            filteredMessages.map((msg, idx) => (
              <MessageBubble key={msg.id || idx} message={msg} personas={personas} />
            ))
          ) : (
            <div className="text-center py-8 text-stone-400">
              {t('report.noMessages')}
            </div>
          )}
        </div>
      </section>

      {/* Recommendations */}
      {finalAnalysis?.actionable_recommendations && finalAnalysis.actionable_recommendations.length > 0 && (
        <section className="bg-gradient-to-br from-amber-600 to-amber-500 rounded-2xl p-8 text-white">
          <h2 className="font-serif text-2xl mb-6 flex items-center gap-3">
            <Check className="w-6 h-6" />
            {t('report.recommendations')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {finalAnalysis.actionable_recommendations.map((rec, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3 p-4 bg-white/10 rounded-xl"
              >
                <ChevronRight className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <p>{rec}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Follow-up Suggestions */}
      {finalAnalysis?.suggested_follow_up && finalAnalysis.suggested_follow_up.length > 0 && (
        <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
          <h2 className="font-serif text-2xl mb-6">{t('report.followUp')}</h2>
          <div className="flex flex-wrap gap-3">
            {finalAnalysis.suggested_follow_up.map((item, idx) => (
              <span
                key={idx}
                className="px-4 py-2 bg-stone-100 text-stone-700 rounded-lg"
              >
                {item}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Participants Detail */}
      <section className="bg-white rounded-2xl p-8 shadow-sm border border-stone-200">
        <h2 className="font-serif text-2xl mb-6 flex items-center gap-3">
          <Users className="w-6 h-6 text-amber-600" />
          {t('report.participantOverview')}
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-200">
                <th className="text-left py-3 px-4 font-medium text-stone-600">{t('report.colName')}</th>
                <th className="text-left py-3 px-4 font-medium text-stone-600">{t('report.colAge')}</th>
                <th className="text-left py-3 px-4 font-medium text-stone-600">{t('report.colRegion')}</th>
                <th className="text-left py-3 px-4 font-medium text-stone-600">{t('report.colEmployment')}</th>
                <th className="text-left py-3 px-4 font-medium text-stone-600">{t('report.colModel')}</th>
                <th className="text-left py-3 px-4 font-medium text-stone-600">{t('report.colPosts')}</th>
                {sessionId && personaChat && (
                  <th className="text-left py-3 px-4 font-medium text-stone-600"></th>
                )}
              </tr>
            </thead>
            <tbody>
              {personas.map(persona => {
                const model = MODELS[persona.model_id] || {};
                const messageCount = messages.filter(m => m.speakerId === persona.id).length;

                return (
                  <tr key={persona.id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="py-3 px-4 font-medium">{persona.name}</td>
                    <td className="py-3 px-4">{persona.age}</td>
                    <td className="py-3 px-4">{persona.region}</td>
                    <td className="py-3 px-4">{persona.employment}</td>
                    <td className="py-3 px-4">
                      <span
                        className="px-2 py-1 rounded-full text-xs text-white"
                        style={{ backgroundColor: model.color || '#888' }}
                      >
                        {model.name || persona.model_id}
                      </span>
                    </td>
                    <td className="py-3 px-4">{messageCount}</td>
                    {sessionId && personaChat && (
                      <td className="py-3 px-4">
                        <button
                          onClick={() => personaChat.startDeepDive(sessionId, persona.id)}
                          className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-100 transition-colors"
                        >
                          {t('deepDive.chatButton')}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Persona Deep Dive Overlay */}
      <AnimatePresence>
        {personaChat && personaChat.activePersonaId && (
          <PersonaDeepDive
            personaChat={personaChat}
            persona={personas.find(p => p.id === personaChat.activePersonaId) || {}}
            sessionId={sessionId}
            topicDimensions={topicDimensions}
            config={config}
          />
        )}
      </AnimatePresence>

      {/* Moderator Chat Overlay */}
      <AnimatePresence>
        {moderatorChat && moderatorChat.isActive && (
          <ModeratorChat
            moderatorChat={moderatorChat}
            sessionId={sessionId}
            moderatorSummary={moderatorSummary}
            config={config}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default App;
